import { useCallback, useEffect, useState } from "react";
import {
  dismissInstallBanner,
  isChromiumInstallEnvironment,
  isInstallBannerDismissed,
  isIosSafari,
  isStandaloneDisplay,
} from "../pwaUtils";
import {
  notifyInstallBannerDismissed,
  notifyNativeInstallAccepted,
} from "../../analytics/integrations/pwaIntegration";

/**
 * Install prompt state: Chromium `beforeinstallprompt`, iOS Add to Home Screen hint.
 */
export function usePwaInstall() {
  const [installed, setInstalled] = useState(() => isStandaloneDisplay());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => isInstallBannerDismissed());
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    setInstalled(isStandaloneDisplay());
    setDismissed(isInstallBannerDismissed());
    setIosHint(isIosSafari() && !isStandaloneDisplay());

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setInstalled(true);
      setIosHint(false);
    };
    const onDisplayMode = () => {
      const standalone = isStandaloneDisplay();
      setInstalled(standalone);
      if (standalone) setIosHint(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia("(display-mode: standalone)");
    mq.addEventListener?.("change", onDisplayMode);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      mq.removeEventListener?.("change", onDisplayMode);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { ok: false, reason: "unavailable" };
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (choice.outcome === "accepted") {
        setInstalled(true);
        notifyNativeInstallAccepted();
        return { ok: true, outcome: choice.outcome };
      }
      return { ok: false, outcome: choice.outcome };
    } catch {
      return { ok: false, reason: "error" };
    }
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    dismissInstallBanner();
    notifyInstallBannerDismissed();
    setDismissed(true);
  }, []);

  const canShowInstall =
    !installed &&
    !dismissed &&
    (Boolean(deferredPrompt) ||
      iosHint ||
      isChromiumInstallEnvironment());

  const menuInstallHint =
    canShowInstall && !deferredPrompt && !iosHint;

  return {
    installed,
    canShowInstall,
    canNativePrompt: Boolean(deferredPrompt),
    menuInstallHint,
    iosHint,
    promptInstall,
    dismiss,
  };
}

/** @typedef {ReturnType<typeof usePwaInstall>} PwaInstallState */
